import React, { useEffect } from 'react';
import { reportBootFailure } from './report-boot-failure';
import { signal } from '@preact/signals-react';
import { ConsentStatusEnum } from '@pypestream/api-services';
import {
  resolveSessionAtBoot,
  sessionService,
  watchSessionVisibility,
} from '@pypestream/api-services/session';
import { Page, PageBody } from '@pypestream/design-system';
import { ErrorState } from '@pypestream/ui-patterns';

import {
  sendGlobalAppEvent,
  sendUserEvent,
  useProStudioCtxSelector,
  useUserCtxSelector,
} from './xstate/app.xstate';
import * as Sentry from '@sentry/react';
import { identifyUser } from '@pypestream/utils';

import { useAnalytics } from './hooks';

/**
 * PSTUDIO-4022. `unavailable` is deliberately not a sign-out: Kratos being
 * unreachable says nothing about the session, and the previous boot code signed
 * the user out on any failed session read.
 */
type SessionBootState = 'checking' | 'active' | 'expired' | 'unavailable';

const sessionState = signal<SessionBootState>('checking');

// The only place that turns "session gone" into a sign-out, so a burst of failed
// operations cannot start several Kratos logout flows. `hadActiveSession` gates
// the `?sessionEnded=true` the login page explains itself with: Kratos answers
// 401 both for an expired session and for a visitor who never had one, and only
// the first is a session that ended.
sessionService.onExpired(({ hadActiveSession }) => {
  sessionState.value = 'expired';
  sendUserEvent('user.signOut', { isSessionEndedEvent: hadActiveSession });
});

resolveSessionAtBoot({ session: sessionService })
  .then((status) => {
    // The urql auth exchange revalidates independently, so it can reach
    // `expired` while the boot read is still retrying an inconclusive answer —
    // and a sign-out already in flight must not be painted over.
    if (sessionState.value !== 'checking') return;

    if (status.state === 'active') {
      sessionState.value = 'active';

      sendGlobalAppEvent({
        type: 'user.signIn',
        user: {
          kratosId: status.identityId,
          // Pass Kratos traits so Produktly can identify the user before GraphQL loads (avoids "User: null" in Slack feedback)
          email: status.traits?.email,
          firstName: status.traits?.name?.first,
          lastName: status.traits?.name?.last,
        },
      });

      return;
    }

    if (status.state === 'unknown') {
      console.error('Could not reach Kratos to verify the session at boot');
      // The user is about to meet a full-screen "we couldn't verify your
      // session" wall and can do nothing but reload. Reported HERE rather than
      // inside the session service, which re-checks in the background and
      // would make this noisy: this is the state the user actually SEES.
      reportBootFailure(
        new Error('session state unknown at boot'),
        'session-boot:unknown'
      );
      sessionState.value = 'unavailable';
    }

    // `expired` is handled by the onExpired subscriber above.
  })
  .catch((error) => {
    if (sessionState.value !== 'checking') return;

    // resolveSessionAtBoot never rejects; belt and braces so a boot-time throw
    // cannot leave the app with no session state at all.
    console.error('Session error:', error);
    reportBootFailure(error, 'session-boot:threw');
    sessionState.value = 'unavailable';
  });

export function AuthProvider({
  children,
}: {
  children: React.PropsWithChildren['children'];
}) {
  const analytics = useAnalytics();

  // Get user data from xstate
  const { optionalConsentStatus, userId, userEmail, userName } =
    useUserCtxSelector((ctx) => ({
      optionalConsentStatus: ctx.user?.optionalConsentStatus,
      userId: ctx.user?.id,
      userEmail: ctx.user?.email,
      userName:
        ctx.user?.firstName && ctx.user?.lastName
          ? `${ctx.user.firstName} ${ctx.user.lastName}`
          : undefined,
    }));
  const {
    enablePostHogSessionRecording,
    customerId,
    orgName,
    projectId,
    solutionName,
  } = useProStudioCtxSelector((ctx) => ({
    enablePostHogSessionRecording:
      ctx.featureFlags?.['post-hog-session-recording'] ?? false,
    customerId: ctx.customerId,
    orgName: ctx.orgName,
    // `extra.platformInfo.projectInfo` used to carry this; without it an event
    // no longer says which project it came from.
    projectId: ctx.project?.id,
    solutionName: ctx.botName,
  }));

  // Sentry counts "users impacted" from whatever `setUser` holds; without this
  // it falls back to the IP address. Signing out clears it, so the next
  // person's errors are not credited to the previous one.
  useEffect(() => {
    identifyUser(
      userId
        ? {
            id: userId,
            email: userEmail,
            customerId,
            orgName,
            projectId,
            solutionName,
          }
        : null
    );
  }, [userId, userEmail, customerId, orgName, projectId, solutionName]);

  useEffect(() => {
    if (!optionalConsentStatus || !analytics) return;

    if (optionalConsentStatus === ConsentStatusEnum.Accepted) {
      if (typeof analytics.opt_in_capturing === 'function') {
        analytics.opt_in_capturing();
      }

      if (userId && typeof analytics.identify === 'function') {
        analytics.identify(userId, {
          email: userEmail,
          name: userName,
          app: 'pro-studio',
        });
      }
    } else {
      if (typeof analytics.opt_out_capturing === 'function') {
        analytics.opt_out_capturing();
      }
    }
  }, [optionalConsentStatus, analytics, userEmail, userId, userName]);

  /**
   * Declining analytics also stops error reporting, the way manager has behaved
   * since before the two copies diverged.
   *
   * Deliberately its OWN effect, keyed on consent alone — the PostHog effect
   * above returns early when `analytics` is null (ad blocker, missing key,
   * network failure), and folding this in there would silently leave Sentry
   * running for a user who declined. Manager's equivalent is shaped the same way
   * (`global-provider.tsx`), which is what makes the two actually mirror.
   *
   * One-way for the session: `close()` shuts the client down, so a later opt-in
   * only takes effect after a reload.
   */
  useEffect(() => {
    if (!optionalConsentStatus) return;
    if (optionalConsentStatus === ConsentStatusEnum.Accepted) return;

    Sentry.close()
      .then(() => {
        console.log('opting out of Sentry');
      })
      .catch(() => {
        // A failed flush must not become an unhandled rejection — the boundary
        // now surfaces those, so it would put the user on an error page while
        // they were opting OUT of error reporting.
      });
  }, [optionalConsentStatus]);

  useEffect(() => {
    if (
      !analytics ||
      typeof analytics.startSessionRecording !== 'function' ||
      typeof analytics.stopSessionRecording !== 'function' ||
      typeof analytics.sessionRecordingStarted !== 'function'
    )
      return;

    if (
      optionalConsentStatus === ConsentStatusEnum.Accepted &&
      enablePostHogSessionRecording &&
      typeof analytics.startSessionRecording === 'function' &&
      typeof analytics.stopSessionRecording === 'function'
    ) {
      if (typeof analytics.startSessionRecording === 'function') {
        analytics.startSessionRecording();
      }
    } else {
      if (typeof analytics.stopSessionRecording === 'function') {
        analytics.stopSessionRecording();
      }
    }
  }, [enablePostHogSessionRecording, optionalConsentStatus, analytics]);

  // An idle tab is exactly how PSTUDIO-4022 was reported: nothing re-checks the
  // session, so the first thing the user sees on return is a broken screen.
  useEffect(() => watchSessionVisibility({ session: sessionService }), []);

  if (sessionState.value === 'unavailable') {
    return (
      <Page>
        <PageBody background="none">
          <ErrorState
            label="We couldn't verify your session"
            body="The sign-in service could not be reached. Check your connection and try again."
            actionLabel="Try again"
            onAction={() => window.location.reload()}
          />
        </PageBody>
      </Page>
    );
  }

  // `checking` and `expired` keep rendering the app as before — a sign-out is a
  // redirect, and blocking here would change first paint for every page.
  return children;
}
